import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, FileDown, ImageDown, MessageSquarePlus,
  Pin, PinOff, Bookmark,
} from "lucide-react";

/**
 * AI Assistant — conversational RAG over the whole yard. Replaces the old
 * Trends page. Grounded in a live structured snapshot (horses, chukkas,
 * riders/dedication, health, feed, invoices, seasons, recent activity) via
 * POST /api/assistant/chat. Gated by `assistant:use`. Any answer can be
 * exported as a branded PDF or image(s).
 */
export default function AssistantPage() {
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content, ts}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starters, setStarters] = useState([]);
  const [exportingIdx, setExportingIdx] = useState(null); // `${i}-pdf` | `${i}-img`
  const [pins, setPins] = useState([]);
  const [pinsOpen, setPinsOpen] = useState(false);
  const [pinningIdx, setPinningIdx] = useState(null);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/starters")
      .then((r) => setStarters(r.data?.starters || []))
      .catch(() => setStarters([]));
    api.get("/assistant/pins")
      .then((r) => setPins(r.data?.pins || []))
      .catch(() => setPins([]));
  }, []);

  useEffect(() => {
    // Keep the newest message in view.
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    const next = [...messages, { role: "user", content: q, ts: Date.now() }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/assistant/chat", {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
      });
      const answer = res.data?.answer || "I don't have an answer for that.";
      setMessages((cur) => [...cur, { role: "assistant", content: answer, ts: Date.now(), error: !!res.data?.error }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "The assistant is unavailable right now.";
      setMessages((cur) => [...cur, { role: "assistant", content: msg, ts: Date.now(), error: true }]);
    } finally {
      setSending(false);
      requestAnimationFrame(() => taRef.current?.focus());
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const questionFor = (idx) => {
    for (let i = idx - 1; i >= 0; i--) if (messages[i].role === "user") return messages[i].content;
    return "Assistant report";
  };

  const exportAnswer = async (idx, kind) => {
    const key = `${idx}-${kind}`;
    if (exportingIdx) return;
    setExportingIdx(key);
    try {
      const question = questionFor(idx);
      const answer = messages[idx].content;
      const lib = await import("@/lib/assistantReport");
      const fn = kind === "pdf" ? lib.downloadAssistantPDF : lib.downloadAssistantImage;
      const { pages } = await fn(answer, { question });
      if (kind === "pdf") toast.success(pages > 1 ? `PDF ready · ${pages} pages` : "PDF downloaded");
      else toast.success(pages > 1 ? `Downloaded ${pages} images` : "Image downloaded");
    } catch (e) {
      console.error("[assistant] export failed:", e);
      toast.error(`Could not create the ${kind === "pdf" ? "PDF" : "image"}: ${e?.message || e}`);
    } finally {
      setExportingIdx(null);
    }
  };

  const reset = () => { setMessages([]); setInput(""); requestAnimationFrame(() => taRef.current?.focus()); };

  const pinAnswer = async (idx) => {
    if (pinningIdx !== null) return;
    setPinningIdx(idx);
    try {
      const res = await api.post("/assistant/pins", {
        question: questionFor(idx),
        answer: messages[idx].content,
      });
      if (res.data?.pin) setPins((cur) => [res.data.pin, ...cur]);
      toast.success("Pinned to your board");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pin this answer");
    } finally {
      setPinningIdx(null);
    }
  };

  const unpin = async (id) => {
    try {
      await api.delete(`/assistant/pins/${id}`);
      setPins((cur) => cur.filter((p) => p.id !== id));
    } catch (_e) {
      toast.error("Could not remove pin");
    }
  };

  const exportPin = async (pin, kind) => {
    const key = `${pin.id}-${kind}`;
    if (exportingIdx) return;
    setExportingIdx(key);
    try {
      const lib = await import("@/lib/assistantReport");
      const fn = kind === "pdf" ? lib.downloadAssistantPDF : lib.downloadAssistantImage;
      const { pages } = await fn(pin.answer, { question: pin.question });
      if (kind === "pdf") toast.success(pages > 1 ? `PDF ready · ${pages} pages` : "PDF downloaded");
      else toast.success(pages > 1 ? `Downloaded ${pages} images` : "Image downloaded");
    } catch (e) {
      toast.error(`Could not create the ${kind === "pdf" ? "PDF" : "image"}`);
    } finally {
      setExportingIdx(null);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-4xl" data-testid="assistant-page">
      <PageHeader
        title="Assistant"
        subtitle="Ask anything about the yard — grounded in your live data"
        actions={
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setPinsOpen(true)} data-testid="assistant-open-pins">
              <Bookmark className="h-4 w-4 mr-2" /> Pinned{pins.length ? ` (${pins.length})` : ""}
            </Button>
            {!empty && (
              <Button variant="ghost" size="sm" onClick={reset} disabled={sending} data-testid="assistant-new-chat">
                <MessageSquarePlus className="h-4 w-4 mr-2" /> New chat
              </Button>
            )}
          </div>
        }
      />

      {/* Transcript */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto rounded-xl border bg-card/40 p-4 space-y-4"
        data-testid="assistant-transcript"
      >
        {empty ? (
          <EmptyState starters={starters} onPick={(s) => send(s)} disabled={sending} />
        ) : (
          messages.map((m, i) => (
            <MessageBubble
              key={m.ts + "-" + i}
              message={m}
              index={i}
              onExport={exportAnswer}
              exportingIdx={exportingIdx}
              onPin={pinAnswer}
              pinningIdx={pinningIdx}
            />
          ))
        )}
        {sending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="assistant-thinking">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--ap-brass)/0.15)]">
              <Sparkles className="h-4 w-4 text-[hsl(var(--ap-brass))]" />
            </span>
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the yard data…
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="mt-3 flex items-end gap-2" data-testid="assistant-composer">
        <textarea
          ref={taRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Ask about horses, riders, health, feed, invoices or seasons…"
          className="flex-1 resize-none rounded-xl border bg-background px-4 py-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ap-brass)/0.5)] max-h-40"
          data-testid="assistant-input"
          disabled={sending}
        />
        <Button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="h-11 px-4 bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
          data-testid="assistant-send"
          aria-label="Send"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Answers are grounded only in your live yard data. The assistant is read-only and won't change anything.
      </p>

      <PinnedBoard
        open={pinsOpen}
        onOpenChange={setPinsOpen}
        pins={pins}
        onUnpin={unpin}
        onExport={exportPin}
        exportingIdx={exportingIdx}
      />
    </div>
  );
}

function PinnedBoard({ open, onOpenChange, pins, onUnpin, onExport, exportingIdx }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="assistant-pins-board">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bookmark className="h-4 w-4 text-[hsl(var(--ap-brass))]" /> Pinned answers
          </SheetTitle>
          <SheetDescription>
            Answers you've saved to revisit. Export any as a branded PDF or image.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {pins.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center" data-testid="assistant-pins-empty">
              No pinned answers yet. Tap <strong>Pin</strong> under any answer to save it here.
            </div>
          ) : (
            pins.map((p) => (
              <Card key={p.id} className="p-3.5" data-testid={`assistant-pin-${p.id}`}>
                {p.question && (
                  <div className="text-[13px] font-medium mb-2 flex items-start gap-2">
                    <Pin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--ap-brass))]" />
                    <span>{p.question}</span>
                  </div>
                )}
                <div className="max-h-56 overflow-y-auto rounded-md bg-muted/40 p-2.5">
                  <Markdown text={p.answer} />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => onExport(p, "pdf")} disabled={!!exportingIdx}
                    data-testid={`assistant-pin-pdf-${p.id}`}>
                    {exportingIdx === `${p.id}-pdf` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
                    PDF
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => onExport(p, "img")} disabled={!!exportingIdx}
                    data-testid={`assistant-pin-img-${p.id}`}>
                    {exportingIdx === `${p.id}-img` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ImageDown className="h-3 w-3 mr-1" />}
                    Image
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground ml-auto hover:text-destructive"
                    onClick={() => onUnpin(p.id)} data-testid={`assistant-unpin-${p.id}`}>
                    <PinOff className="h-3 w-3 mr-1" /> Unpin
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EmptyState({ starters, onPick, disabled }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="assistant-empty">
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--ap-brass)/0.12)] mb-4">
        <Sparkles className="h-7 w-7 text-[hsl(var(--ap-brass))]" />
      </span>
      <h3 className="font-serif text-2xl">Ask the yard anything</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-md">
        I draw on horses, chukkas, riders, health logs, feed, invoices and seasons to answer — and I'll tell you when something isn't in the data.
      </p>
      {starters.length > 0 && (
        <div className="mt-6 grid gap-2 sm:grid-cols-2 w-full max-w-2xl" data-testid="assistant-starters">
          {starters.map((s, i) => (
            <button
              key={i}
              onClick={() => onPick(s)}
              disabled={disabled}
              className="text-left rounded-lg border p-3 text-sm hover:bg-muted/50 hover:border-[hsl(var(--ap-brass)/0.5)] transition-colors disabled:opacity-50"
              data-testid={`assistant-starter-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message, index, onExport, exportingIdx, onPin, pinningIdx }) {
  const isUser = message.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid={`assistant-msg-user-${index}`}>
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[hsl(var(--ap-forest))] text-white px-4 py-2.5 text-sm whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    );
  }
  const pdfKey = `${index}-pdf`, imgKey = `${index}-img`;
  return (
    <div className="flex gap-3" data-testid={`assistant-msg-assistant-${index}`}>
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ap-brass)/0.15)]">
        <Sparkles className="h-4 w-4 text-[hsl(var(--ap-brass))]" />
      </span>
      <div className="min-w-0 flex-1">
        <Card className={`rounded-2xl rounded-tl-sm p-4 ${message.error ? "border-[hsl(var(--ap-warning)/0.5)] bg-[hsl(var(--ap-warning)/0.06)]" : ""}`}>
          <Markdown text={message.content} />
        </Card>
        {!message.error && (
          <div className="mt-1.5 flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => onExport(index, "pdf")} disabled={!!exportingIdx}
              data-testid={`assistant-export-pdf-${index}`}>
              {exportingIdx === pdfKey ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
              PDF
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => onExport(index, "img")} disabled={!!exportingIdx}
              data-testid={`assistant-export-img-${index}`}>
              {exportingIdx === imgKey ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ImageDown className="h-3 w-3 mr-1" />}
              Image
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => onPin(index)} disabled={pinningIdx !== null}
              data-testid={`assistant-pin-${index}`}>
              {pinningIdx === index ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Pin className="h-3 w-3 mr-1" />}
              Pin
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Lightweight Markdown renderer (headings, bold, bullets, numbers,
//    pipe tables, paragraphs). Intentionally small — the assistant is
//    instructed to emit only these constructs.
function _inline(text) {
  const parts = String(text ?? "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
    return <span key={i}>{p}</span>;
  });
}

function Markdown({ text }) {
  const lines = String(text ?? "").replace(/\r/g, "").split("\n");
  const blocks = [];
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (t === "") continue;

    // Table
    if (t.startsWith("|") && t.includes("|", 1)) {
      const tbl = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { tbl.push(lines[i].trim()); i++; }
      i--;
      const rows = tbl.map((l) => l.replace(/^\||\|$/g, "").split("|").map((c) => c.trim()))
        .filter((r) => !r.every((c) => /^:?-{2,}:?$/.test(c) || c === ""));
      if (rows.length) {
        const headers = rows[0]; const body = rows.slice(1);
        blocks.push(
          <div key={blocks.length} className="my-2 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[hsl(var(--ap-ink,222_15%_12%))]">
                  {headers.map((h, j) => (
                    <th key={j} className="text-left font-semibold text-[11px] uppercase tracking-wider px-2.5 py-1.5 bg-foreground text-background">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((r, ri) => (
                  <tr key={ri} className={ri % 2 ? "bg-muted/40" : ""}>
                    {r.map((c, ci) => (
                      <td key={ci} className={`px-2.5 py-1.5 align-top ${ci === 0 ? "font-medium" : "tabular-nums"}`}>{_inline(c)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // Headings
    if (/^#{1,3}\s+/.test(t)) {
      const level = (t.match(/^#+/) || ["#"])[0].length;
      const content = t.replace(/^#{1,3}\s+/, "");
      const cls = level === 1 ? "text-lg font-serif mt-2" : "text-base font-serif mt-2";
      blocks.push(<p key={blocks.length} className={`${cls} font-semibold`}>{_inline(content)}</p>);
      continue;
    }

    // Bullet group
    if (/^[-*]\s+/.test(t)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^[-*]\s+/, "")); i++; }
      i--;
      blocks.push(
        <ul key={blocks.length} className="my-1.5 space-y-1 list-none">
          {items.map((it, j) => (
            <li key={j} className="flex gap-2 text-sm leading-relaxed">
              <span className="text-[hsl(var(--ap-brass))] mt-0.5">•</span><span>{_inline(it)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered group
    if (/^\d+\.\s+/.test(t)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+\.\s+/, "")); i++; }
      i--;
      blocks.push(
        <ol key={blocks.length} className="my-1.5 space-y-1 list-decimal pl-5 text-sm leading-relaxed">
          {items.map((it, j) => <li key={j}>{_inline(it)}</li>)}
        </ol>
      );
      continue;
    }

    // Paragraph
    blocks.push(<p key={blocks.length} className="text-sm leading-relaxed my-1">{_inline(t)}</p>);
  }
  return <div className="assistant-markdown" data-testid="assistant-answer">{blocks}</div>;
}
